import { Component, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * Base component that provides automatic subscription cleanup on component destruction.
 *
 * This abstract class solves the common problem of memory leaks from unmanaged RxJS subscriptions
 * in Angular components. By extending this class, components get access to a `destroy$` subject
 * that automatically emits and completes when the component is destroyed.
 *
 * @example
 * ```typescript
 * import { Component } from '@angular/core';
 * import { takeUntil } from 'rxjs';
 * import { ComponentWithDestroy } from '@shared/component-with-destroy';
 *
 * @Component({
 *   selector: 'app-my-component',
 *   template: '...'
 * })
 * export class MyComponent extends ComponentWithDestroy {
 *   constructor(private dataService: DataService) {
 *     super();
 *   }
 *
 *   ngOnInit() {
 *     this.dataService.getData()
 *       .pipe(takeUntil(this.destroy$))
 *       .subscribe(data => console.log(data));
 *   }
 * }
 * ```
 */
@Component({ template: '' })
// Named for what it does (provides destroy$ to subclasses), not as a renderable component in
// its own right - renaming to fit the *Component suffix would obscure its actual purpose.
// eslint-disable-next-line @angular-eslint/component-class-suffix
export abstract class ComponentWithDestroy implements OnDestroy {
  /**
   * Subject that emits when the component is being destroyed.
   * Use with `takeUntil()` operator to automatically unsubscribe from observables.
   */
  protected destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
